// src/modulos/Usuarios/UserReportPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { auth, db } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    query,
    orderBy,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp
} from 'firebase/firestore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import useFirestore from '../../hooks/useFirestore';
import {
    FormModal,
    ConfirmModal,
    PageHeader,
    Toast,
    Pagination
} from '../../components/common';
import styles from '../Users/UsersPage.module.css';

const USERS_COLLECTION = 'users';
const REPORT_HISTORY_COLLECTION = 'user-report-history';
const ROLES_COLLECTION = 'parameters/data/cargos';

const ERP_ROLES = [
    { value: 'Administracao', label: 'Administração' },
    { value: 'Comercial', label: 'Comercial' },
    { value: 'Diretoria', label: 'Diretoria (Admin)' },
    { value: 'Laboratorio', label: 'Laboratório' },
    { value: 'Logistica', label: 'Logística' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'TI', label: 'TI' },
];

const UserReportPage = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        role: '',
        cargoId: '',
        status: 'all',
        searchTerm: '',
        includeInactive: false,
        dateFrom: '',
        dateTo: ''
    });

    // Fetch users
    const { data: users, loading: usersLoading } = useFirestore(USERS_COLLECTION, 'nome');
    const { data: cargos, loading: cargosLoading } = useFirestore(ROLES_COLLECTION, 'name');

    // Get current user
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    // Load report history
    useEffect(() => {
        loadReports();
    }, []);

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    const loadReports = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, REPORT_HISTORY_COLLECTION),
                orderBy('generatedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const reportsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                generatedAt: doc.data().generatedAt?.toDate() || new Date()
            }));
            setReports(reportsList);
        } catch (error) {
            console.error("Error loading report history:", error);
            addToast('Erro ao carregar histórico', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (reportId) => {
        setReportToDelete(reportId);
        setIsConfirmOpen(true);
    };

    const deleteReport = async () => {
        if (!reportToDelete) return;

        try {
            await deleteDoc(doc(db, REPORT_HISTORY_COLLECTION, reportToDelete));
            setReports(reports.filter(r => r.id !== reportToDelete));
            addToast('Relatório excluído com sucesso', 'success');
        } catch (error) {
            console.error("Error deleting report:", error);
            addToast('Erro ao excluir relatório', 'error');
        } finally {
            setIsConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    // Apply filters to users
    const filteredUsers = useMemo(() => {
        if (!users) return [];

        let filtered = [...users];

        // Filter by status
        if (filters.status !== 'all') {
            filtered = filtered.filter(user => user.status === filters.status);
        } else if (!filters.includeInactive) {
            filtered = filtered.filter(user => user.status !== 'inactive');
        }

        // Filter by ERP role
        if (filters.role) {
            filtered = filtered.filter(user => user.role === filters.role);
        }

        // Filter by cargo (position)
        if (filters.cargoId) {
            filtered = filtered.filter(user => user.cargo === filters.cargoId);
        }

        // Filter by search term (name, email)
        if (filters.searchTerm) {
            const term = filters.searchTerm.toLowerCase();
            filtered = filtered.filter(user =>
                (user.nome || '').toLowerCase().includes(term) ||
                (user.email || '').toLowerCase().includes(term)
            );
        }

        // Filter by creation date range
        if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(user => {
                const createdAt = user.createdAt ? new Date(user.createdAt) : null;
                return createdAt && createdAt >= fromDate;
            });
        }

        if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(user => {
                const createdAt = user.createdAt ? new Date(user.createdAt) : null;
                return createdAt && createdAt <= toDate;
            });
        }

        // Sort by name
        filtered.sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));

        return filtered;
    }, [users, filters]);

    const getRoleLabel = (roleValue) => {
        return ERP_ROLES.find(r => r.value === roleValue)?.label || roleValue || 'Básico';
    };

    const getCargoName = (cargoId) => {
        return cargos?.find(c => c.id === cargoId)?.name || null;
    };

    const generateExcelReport = async (items, filtersObj, reportTitle, fileName) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Usuários');

        // Title row
        worksheet.mergeCells('A1:G1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = reportTitle;
        titleCell.font = { size: 16, bold: true };
        titleCell.alignment = { horizontal: 'center' };

        // Generation info
        worksheet.mergeCells('A2:G2');
        worksheet.getCell('A2').value = `Gerado em: ${new Date().toLocaleString('pt-BR')}`;
        worksheet.getCell('A2').font = { size: 10, italic: true };

        worksheet.mergeCells('A3:G3');
        worksheet.getCell('A3').value = `Gerado por: ${currentUser?.email || 'Usuário'}`;
        worksheet.getCell('A3').font = { size: 10, italic: true };

        // Filter info
        let filterRow = 4;
        if (Object.keys(filtersObj).some(key => filtersObj[key])) {
            const filterParts = [];
            if (filtersObj.roleLabel) filterParts.push(`Perfil: ${filtersObj.roleLabel}`);
            if (filtersObj.cargoName) filterParts.push(`Cargo: ${filtersObj.cargoName}`);
            if (filtersObj.statusLabel) filterParts.push(`Status: ${filtersObj.statusLabel}`);
            if (filtersObj.dateFrom) filterParts.push(`De: ${filtersObj.dateFrom}`);
            if (filtersObj.dateTo) filterParts.push(`Até: ${filtersObj.dateTo}`);
            if (filtersObj.searchTerm) filterParts.push(`Busca: ${filtersObj.searchTerm}`);

            if (filterParts.length > 0) {
                worksheet.mergeCells(`A${filterRow}:G${filterRow}`);
                worksheet.getCell(`A${filterRow}`).value = `Filtros: ${filterParts.join(' | ')}`;
                worksheet.getCell(`A${filterRow}`).font = { size: 10, italic: true };
                filterRow++;
            }
        }

        // Empty row
        filterRow++;

        // Headers
        const headers = ['Nome', 'Email', 'Perfil ERP', 'Cargo', 'Status', 'Data Cadastro', 'Última Atualização'];
        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF007bff' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // Data rows
        items.forEach(user => {
            const row = worksheet.addRow([
                user.nome || 'N/A',
                user.email || 'N/A',
                getRoleLabel(user.role),
                getCargoName(user.cargo) || 'Não definido',
                user.status === 'active' ? 'Ativo' : 'Inativo',
                user.createdAt ? new Date(user.createdAt).toLocaleDateString('pt-BR') : 'N/A',
                user.updatedAt ? new Date(user.updatedAt).toLocaleDateString('pt-BR') : 'N/A'
            ]);

            row.eachCell((cell, colNumber) => {
                cell.alignment = { vertical: 'middle' };
                if (colNumber !== 1) {
                    cell.alignment = { ...cell.alignment, horizontal: 'center' };
                }
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // Color status cell
            const statusCell = row.getCell(5);
            if (user.status === 'active') {
                statusCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFd4edda' }
                };
            } else {
                statusCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFF8D7DA' }
                };
            }
        });

        // Summary row
        const summaryRow = worksheet.addRow([
            `Total de usuários: ${items.length}`,
            '', '', '', '', '', ''
        ]);
        summaryRow.getCell(1).font = { bold: true };
        worksheet.mergeCells(`A${worksheet.rowCount}:G${worksheet.rowCount}`);

        // Auto-size columns
        worksheet.columns.forEach(column => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, cell => {
                const cellValue = cell.value ? cell.value.toString() : '';
                maxLength = Math.max(maxLength, cellValue.length);
            });
            column.width = Math.min(maxLength + 2, 40);
        });

        // Generate file
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    const generateReport = async () => {
        if (!currentUser) {
            addToast('Você precisa estar logado para gerar relatórios', 'error');
            return;
        }

        if (filteredUsers.length === 0) {
            addToast('Nenhum usuário encontrado com os filtros selecionados.', 'warning');
            return;
        }

        setGenerating(true);
        addToast('Gerando relatório...', 'info');

        try {
            // Build filter display names
            const filterDisplay = {};

            if (filters.role) {
                filterDisplay.roleLabel = getRoleLabel(filters.role);
            }
            if (filters.cargoId) {
                filterDisplay.cargoName = getCargoName(filters.cargoId);
            }
            if (filters.status !== 'all') {
                filterDisplay.statusLabel = filters.status === 'active' ? 'Ativos' : 'Inativos';
            } else if (!filters.includeInactive) {
                filterDisplay.statusLabel = 'Ativos (exceto inativos)';
            }
            if (filters.dateFrom) {
                filterDisplay.dateFrom = new Date(filters.dateFrom).toLocaleDateString('pt-BR');
            }
            if (filters.dateTo) {
                filterDisplay.dateTo = new Date(filters.dateTo).toLocaleDateString('pt-BR');
            }
            if (filters.searchTerm) {
                filterDisplay.searchTerm = filters.searchTerm;
            }

            // Build report title
            let reportTitle = 'RELATÓRIO DE USUÁRIOS';
            if (filterDisplay.roleLabel) reportTitle += ` - ${filterDisplay.roleLabel}`;
            if (filterDisplay.statusLabel && filterDisplay.statusLabel !== 'Ativos (exceto inativos)') {
                reportTitle += ` - ${filterDisplay.statusLabel}`;
            }

            // Build filename
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filterParts = [];
            if (filterDisplay.roleLabel) filterParts.push(filterDisplay.roleLabel.replace(/\s/g, '_'));
            if (filterDisplay.statusLabel && filterDisplay.statusLabel !== 'Ativos (exceto inativos)') {
                filterParts.push(filterDisplay.statusLabel.replace(/\s/g, '_'));
            }
            const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
            const fileName = `Relatorio_Usuarios${filterSuffix}_${timestamp}.xlsx`;

            // Generate Excel file
            await generateExcelReport(
                filteredUsers,
                filterDisplay,
                reportTitle,
                fileName
            );

            // Save to history
            const reportData = {
                fileName,
                generatedAt: serverTimestamp(),
                generatedBy: currentUser.email || currentUser.displayName || currentUser.uid,
                generatedByEmail: currentUser.email,
                generatedByName: currentUser.displayName || '',
                userCount: filteredUsers.length,
                filters: {
                    role: filters.role,
                    roleLabel: filterDisplay.roleLabel || null,
                    cargoId: filters.cargoId,
                    cargoName: filterDisplay.cargoName || null,
                    status: filters.status,
                    statusLabel: filterDisplay.statusLabel || null,
                    includeInactive: filters.includeInactive,
                    dateFrom: filters.dateFrom || null,
                    dateTo: filters.dateTo || null,
                    searchTerm: filters.searchTerm || null
                }
            };

            const docRef = await addDoc(collection(db, REPORT_HISTORY_COLLECTION), reportData);

            setReports([
                {
                    id: docRef.id,
                    ...reportData,
                    generatedAt: new Date()
                },
                ...reports
            ]);

            addToast(`Relatório "${fileName}" gerado com sucesso!`, 'success');
            setIsModalOpen(false);

        } catch (error) {
            console.error('Error generating report:', error);
            addToast(`Erro ao gerar relatório: ${error.message}`, 'error');
        } finally {
            setGenerating(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleResetFilters = () => {
        setFilters({
            role: '',
            cargoId: '',
            status: 'all',
            searchTerm: '',
            includeInactive: false,
            dateFrom: '',
            dateTo: ''
        });
    };

    const formatDate = (date) => {
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFilterDescription = (filtersObj) => {
        const parts = [];
        if (filtersObj.roleLabel) parts.push(`Perfil: ${filtersObj.roleLabel}`);
        if (filtersObj.cargoName) parts.push(`Cargo: ${filtersObj.cargoName}`);
        if (filtersObj.statusLabel) parts.push(`Status: ${filtersObj.statusLabel}`);
        if (filtersObj.dateFrom) parts.push(`De: ${filtersObj.dateFrom}`);
        if (filtersObj.dateTo) parts.push(`Até: ${filtersObj.dateTo}`);
        if (filtersObj.searchTerm) parts.push(`Busca: ${filtersObj.searchTerm}`);
        if (parts.length === 0) return 'Todos os usuários';
        return parts.join(', ');
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        handleResetFilters();
    };

    const isLoading = usersLoading || cargosLoading;

    // Check if any filter is active
    const hasActiveFilters = filters.role || filters.cargoId || filters.status !== 'all' ||
        filters.searchTerm || filters.includeInactive || filters.dateFrom || filters.dateTo;

    // Render filter form content for modal
    const renderFilterForm = () => (
        <>
            {/* Filter by ERP Role */}
            <div className={styles.formGroup}>
                <label>Perfil ERP</label>
                <select
                    value={filters.role}
                    onChange={(e) => handleFilterChange('role', e.target.value)}
                    className={styles.select}
                >
                    <option value="">Todos os perfis</option>
                    {ERP_ROLES.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                </select>
            </div>

            {/* Filter by Cargo */}
            <div className={styles.formGroup}>
                <label>Cargo (Posição)</label>
                <select
                    value={filters.cargoId}
                    onChange={(e) => handleFilterChange('cargoId', e.target.value)}
                    className={styles.select}
                    disabled={cargosLoading}
                >
                    <option value="">Todos os cargos</option>
                    {cargos?.map(cargo => (
                        <option key={cargo.id} value={cargo.id}>{cargo.name}</option>
                    ))}
                </select>
            </div>

            {/* Status Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Status</label>
                    <select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className={styles.select}
                    >
                        <option value="all">Todos</option>
                        <option value="active">Ativos</option>
                        <option value="inactive">Inativos</option>
                    </select>
                </div>
                <div className={styles.formGroup}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '28px' }}>
                        <input
                            type="checkbox"
                            checked={filters.includeInactive}
                            onChange={(e) => handleFilterChange('includeInactive', e.target.checked)}
                            disabled={filters.status !== 'all'}
                        />
                        Incluir usuários inativos (quando "Todos")
                    </label>
                </div>
            </div>

            {/* Date Range Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Data Cadastro (Início)</label>
                    <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className={styles.input}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Data Cadastro (Fim)</label>
                    <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className={styles.input}
                    />
                </div>
            </div>

            {/* Search Term */}
            <div className={styles.formGroup}>
                <label>Busca por Nome ou Email</label>
                <input
                    type="text"
                    placeholder="Digite nome ou email..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    className={styles.input}
                />
            </div>

            {/* Preview Info */}
            <div className={styles.fieldHint} style={{ textAlign: 'center', padding: '8px', background: 'var(--primary-light)', borderRadius: '6px' }}>
                📊 {filteredUsers.length} {filteredUsers.length === 1 ? 'usuário encontrado' : 'usuários encontrados'} com os filtros atuais
            </div>
        </>
    );

    return (
        <div className={styles.pageContainer}>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={deleteReport}
                title="Excluir Relatório"
                message="Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita."
                confirmText="Excluir"
                danger
            />

            {/* Filter Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={generateReport}
                title="Gerar Relatório de Usuários"
                subtitle="Selecione os filtros desejados para o relatório"
                isLoading={generating}
                submitText={`Gerar Relatório (${filteredUsers.length} usuários)`}
                cancelText="Cancelar"
                maxWidth="700px"
            >
                {renderFilterForm()}
                <div className={styles.formActions} style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                    <button type="button" onClick={handleResetFilters} className={styles.cancelButton}>
                        Limpar Filtros
                    </button>
                </div>
            </FormModal>

            {/* Main Header */}
            <PageHeader
                title="Relatório de Usuários"
                actionText="Novo Relatório"
                onAction={openModal}
                disabled={!currentUser || isLoading}
            />

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <div className={styles.activeFiltersContainer}>
                    <span className={styles.activeFiltersLabel}>Filtros ativos:</span>
                    {filters.role && (
                        <span className={styles.filterBadge}>
                            Perfil: {getRoleLabel(filters.role)}
                        </span>
                    )}
                    {filters.cargoId && cargos?.find(c => c.id === filters.cargoId) && (
                        <span className={styles.filterBadge}>
                            Cargo: {cargos.find(c => c.id === filters.cargoId)?.name}
                        </span>
                    )}
                    {filters.status !== 'all' && (
                        <span className={styles.filterBadge}>
                            Status: {filters.status === 'active' ? 'Ativos' : 'Inativos'}
                        </span>
                    )}
                    {filters.includeInactive && filters.status === 'all' && (
                        <span className={styles.filterBadge}>
                            Incluindo inativos
                        </span>
                    )}
                    {filters.dateFrom && (
                        <span className={styles.filterBadge}>
                            📅 De: {new Date(filters.dateFrom).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                    {filters.dateTo && (
                        <span className={styles.filterBadge}>
                            📅 Até: {new Date(filters.dateTo).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                    {filters.searchTerm && (
                        <span className={styles.filterBadge}>
                            🔍 Busca: {filters.searchTerm}
                        </span>
                    )}
                </div>
            )}

            {/* Report History */}
            <div className={styles.tableContainer}>
                <h3 className={styles.tableTitle} style={{ padding: '16px 20px', margin: 0, borderBottom: '1px solid var(--border-color)' }}>
                    Histórico de Relatórios Gerados
                </h3>
                <div style={{ overflowX: 'auto' }}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '160px' }}>Data/Hora</th>
                                <th>Nome do Arquivo</th>
                                <th style={{ width: '200px' }}>Gerado por</th>
                                <th style={{ width: '80px' }}>Usuários</th>
                                <th style={{ width: '250px' }}>Filtros Aplicados</th>
                                <th style={{ width: '80px' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        <div className={styles.spinner} style={{ margin: '0 auto' }}></div>
                                        <span style={{ marginTop: '8px', display: 'block' }}>Carregando histórico...</span>
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        Nenhum relatório gerado ainda. Clique em "Novo Relatório" para começar.
                                    </td>
                                </tr>
                            ) : (
                                reports.map((report) => (
                                    <tr key={report.id}>
                                        <td>{formatDate(report.generatedAt)}</td>
                                        <td>
                                            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                                {report.fileName}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <span style={{ fontWeight: 500 }}>
                                                    {report.generatedByName || report.generatedBy?.split('@')[0] || 'Usuário'}
                                                </span>
                                                {report.generatedByEmail && (
                                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                                        {report.generatedByEmail}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{report.userCount}</td>
                                        <td style={{ fontSize: '12px' }}>
                                            {getFilterDescription(report.filters)}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleDeleteClick(report.id)}
                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                style={{ padding: '4px 12px', fontSize: '12px' }}
                                            >
                                                Excluir
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default UserReportPage;